import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { modelService } from "@/lib/services";
import type { AIModel, ModelReferences } from "@/types";

export type ReassignAction = "delete" | "disable" | "move";

const TITLES: Record<ReassignAction, string> = {
  delete: "Delete Model",
  disable: "Disable Model",
  move: "Move to Another Model",
};

const MOVE_LABELS: Record<ReassignAction, string> = {
  delete: "Move and delete",
  disable: "Move and disable",
  move: "Move",
};

const DELETE_WARNING =
  "This action cannot be undone. If this model is assigned to a tier default, the tier will become unassigned.";

const NEXT_STEPS: Record<ReassignAction, string> = {
  delete: `Move them to another model before deleting it. ${DELETE_WARNING}`,
  disable: "Move them to another model before disabling it.",
  move: "",
};

const countOf = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

export const describeUsage = (agents: number, chats: number) =>
  `${countOf(agents, "agent")} and ${countOf(chats, "chat")}`;

export const replacementCandidates = (model: AIModel, models: AIModel[]) =>
  models.filter((m) => m.id !== model.id && m.modelType === model.modelType && m.isEnabled && !m.isDeprecated);

export interface ReassignModelModalProps {
  model: AIModel;
  models: AIModel[];
  action: ReassignAction;
  references: ModelReferences;
  onClose: () => void;
  onDone: () => void;
}

export function ReassignModelModal({
  model,
  models,
  action,
  references,
  onClose,
  onDone,
}: Readonly<ReassignModelModalProps>) {
  const { showToast } = useToast();
  const candidates = replacementCandidates(model, models);
  const [replacementId, setReplacementId] = useState(
    () => candidates.find((m) => m.id === model.replacementModelId)?.id ?? "",
  );
  const [moving, setMoving] = useState(false);
  const { affectedAgents, chatCount } = references;
  const inUse = affectedAgents.length > 0 || chatCount > 0;

  const handleMove = async () => {
    try {
      setMoving(true);
      const res = await modelService.reassign(model.id, replacementId);
      showToast(res.message, "success");
      onDone();
    } catch (err) {
      console.error("Failed to move model references:", err);
      showToast(getApiErrorMessage(err, "Failed to move agents and chats"), "error");
      setMoving(false);
    }
  };

  return (
    <dialog // NOSONAR — native <dialog> requires showModal()/close() lifecycle management; using open attribute with custom overlay instead
      open
      className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4 m-0 max-w-none max-h-none w-full h-full border-none"
      aria-modal="true"
      aria-labelledby="reassign-model-title"
    >
      <div className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-md p-6">
        <h3 id="reassign-model-title" className="text-lg font-semibold text-content-primary mb-2">
          {TITLES[action]}
        </h3>
        {/* Only delete opens this dialog for a model nothing uses. */}
        {!inUse && (
          <p className="text-sm text-content-secondary mb-4">
            Are you sure you want to delete <span className="font-medium">{model.name}</span>? {DELETE_WARNING}
          </p>
        )}
        {inUse && (
          <>
            <p className="text-sm text-content-secondary mb-3">
              <span className="font-medium">{model.name}</span> is used by{" "}
              {describeUsage(affectedAgents.length, chatCount)}. {NEXT_STEPS[action]}
            </p>
            {affectedAgents.length > 0 && (
              <ul className="mb-3 max-h-32 overflow-y-auto space-y-1 rounded-lg border border-border-primary bg-surface-secondary px-3 py-2 text-sm text-content-primary">
                {affectedAgents.map((agent) => (
                  <li key={agent.uuid}>{agent.name}</li>
                ))}
              </ul>
            )}
            {candidates.length > 0 ? (
              <div className="mb-4">
                <label
                  htmlFor="reassign-model-target"
                  className="block text-sm font-medium text-content-secondary mb-1.5"
                >
                  Move to
                </label>
                <select
                  id="reassign-model-target"
                  value={replacementId}
                  onChange={(e) => setReplacementId(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                >
                  <option value="">Select a model</option>
                  {candidates.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="mb-4 text-sm text-amber-800 dark:text-amber-200">
                No active {model.modelType} model available.
              </p>
            )}
          </>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            disabled={moving}
            className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          {inUse && (
            <button
              onClick={handleMove}
              disabled={!replacementId || moving}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                action === "delete"
                  ? "bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                  : "bg-brand-gradient text-white hover:opacity-90"
              }`}
            >
              {moving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                  Moving...
                </>
              ) : (
                MOVE_LABELS[action]
              )}
            </button>
          )}
          {!inUse && (
            <button
              onClick={onDone}
              className="flex-1 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg font-medium transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}
